import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import { API } from '@/lib/api';
import { useResizablePanel } from '@/hooks/useResizablePanel';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { readEventStream } from '@/lib/sse';
import LinkPreviewPanel from '@/components/LinkPreviewPanel';
import KnowledgeSourceBadge from '@/components/KnowledgeSourceBadge';
import JollyDictationButton from '@/components/JollyDictationButton';
import FeatureHistoryMenu from '@/components/FeatureHistoryMenu';
import SourceTrustBadge from '@/components/SourceTrustBadge';
import BrandLogo from '@/components/BrandLogo';
import { ensureAgentWorkspace, fetchAgentWorkspace } from '@/lib/agentWorkspace';
import { buildAgentDestination } from '@/lib/agentRouting';
import { buildChatWorkflowPlan } from '@/lib/chatOrchestration';
import { buildStructuredMarkdownSections } from '@/lib/markdownExport';
import {
  buildChatContinuityKey,
  listJourneyRoutes,
  loadChatUiSnapshot,
  recordJourneyVisit,
  restoreChatMessagesWithUiState,
  saveChatUiSnapshot,
  updateChatUiSnapshotMeta,
} from '@/lib/chatJourney';
import {
  canRestartFromDraft,
  finalizeAssistantContent,
  removeActiveTurnMessages,
  updateMessageByLocalId as updateMessageListByLocalId,
} from '@/lib/chatStreamState';
import {
  buildTrustSummary,
  dedupeSourceList,
  getSourceDisplayTitle,
  getSourcePreviewHref,
} from '@/lib/sourceTrust';
import { Send, Search, FileText, BookOpen, Sword, Users, GraduationCap, ThumbsUp, ThumbsDown, Copy, Bookmark, X, ExternalLink, Loader2, PanelRightOpen, PanelRightClose, Plus, Square, Paperclip, CheckCircle, FolderOpen, History, MessageSquare, Link2, ChevronDown, Trash2, Mic, Eye, Wand2 } from 'lucide-react';

const MODES = [
  { value: 'RESEARCH', label: 'Research Draft', icon: Search, desc: 'Pull authorities, isolate the ratio, and keep a reliable trail for mention or drafting.' },
  { value: 'DRAFT', label: 'Filing', icon: FileText, desc: 'Shape notices, petitions, replies, and written submissions from the same matter record.' },
  { value: 'EXPLAIN', label: 'Quick Explain', icon: BookOpen, desc: 'Translate a doctrine or judgment into chamber-ready language for clients and juniors.' },
  { value: 'STRATEGY', label: 'Hearing Prep', icon: Sword, desc: 'Map likely objections, rebuttals, and bench questions before the next date.' },
  { value: 'EXAM_PREP', label: 'Study', icon: GraduationCap, desc: 'Turn a topic into revision notes, drills, and answer structures.' },
];

const SAMPLE_QUESTIONS = [
  'Read my uploaded brief, explain the strongest jurisdiction point in Hindi, and prepare a short hearing note with authorities.',
  'Turn this client call into a Section 138 reply strategy and draft the first notice with missing facts flagged.',
  'Prepare oral submissions resisting a stay in an Article 226 writ petition and tell me when this should move into Aina.',
];

const WORKFLOW_STARTS = [
  {
    title: 'Start with a live matter',
    badge: 'Front door',
    mode: 'STRATEGY',
    description: 'Describe the problem in your own words, then let Advoq decide whether this remains in Chat or needs a deeper handoff.',
    prompt: 'I have a hearing tomorrow on interim relief in a trademark suit. Read the uploaded brief, isolate the strongest points, and prepare oral submissions.',
  },
  {
    title: 'Draft from facts and files',
    badge: 'Drafting',
    mode: 'DRAFT',
    description: 'Move from client facts, uploaded documents, or chamber notes into a first draft without rebuilding context.',
    prompt: 'Draft a reply notice for a Section 138 Negotiable Instruments Act matter using the uploaded papers, and list the facts still missing.',
  },
  {
    title: 'Ground the issue in law',
    badge: 'Research',
    mode: 'RESEARCH',
    description: 'When the next move is authority-heavy, Advoq can turn the question into a verified research trail instead of tab overload.',
    prompt: 'Build a verified research trail on territorial jurisdiction in arbitration matters and separate binding Supreme Court authority from persuasive High Court cases.',
  },
  {
    title: 'Start from voice',
    badge: 'Voice',
    mode: 'EXPLAIN',
    description: 'Use Jolly for dictation or debrief, then continue the same matter in chat, research, or drafting without losing the spoken context.',
    prompt: 'I just dictated a hearing debrief. Turn it into chamber-ready notes, flag next steps, and prepare a concise client update.',
  },
];

function getUploadPreviewUrl(uploadId) {
  return `${API}/uploads/${uploadId}/preview`;
}

// Replace citation names in content with markdown links, longest match first
function applyInlineCitations(content, citations) {
  if (!citations?.length || !content) return content;
  const sorted = [...citations]
    .filter(citation => citation.verified && citation.ikanoon_url)
    .sort((a, b) => (b.raw_text?.length || 0) - (a.raw_text?.length || 0));
  let result = content;
  for (const cit of sorted) {
    if (!cit.raw_text || !cit.ikanoon_url) continue;
    const url = cit.ikanoon_url;
    const escaped = cit.raw_text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      // Don't re-link text that's already inside a markdown link
      result = result.replace(new RegExp(`(?<!\\[)${escaped}(?![^[\\]]*\\])`, 'g'), `[${cit.raw_text}](${url})`);
    } catch { /* skip if regex fails */ }
  }
  return result;
}

// Extract case citations client-side during streaming (provisional, unverified)
function extractCitationsClient(text) {
  if (!text) return [];
  const citations = [];
  const seen = new Set();
  // Case law: "Name v. Name" or "Name vs. Name" patterns
  const casePattern = /\b([A-Z][a-zA-Z\s&.'-]{2,45}\s+v(?:s)?\.?\s+[A-Z][a-zA-Z\s&.'-]{2,45}(?:\s*\(\d{4}\))?)/g;
  for (const match of text.matchAll(casePattern)) {
    const raw = match[1].trim().replace(/[.,;:]+$/, '');
    if (!seen.has(raw) && raw.length > 8 && raw.length < 130) {
      seen.add(raw);
      citations.push({ raw_text: raw, type: 'CASE_LAW', verified: false });
    }
  }
  return citations.slice(0, 15);
}

function makeLocalMessageId(prefix = 'msg') {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

const PRIMARY_BUTTON_CLASS = 'advoq-btn-primary text-sm';
const SECONDARY_BUTTON_CLASS = 'advoq-btn-secondary text-sm';
const DANGER_BUTTON_CLASS = 'advoq-btn-danger text-sm';
const GHOST_BUTTON_CLASS = 'advoq-btn-ghost text-sm';

function PickerListSkeleton() {
  return (
    <div className="animate-pulse space-y-3 py-2">
      {[0, 1, 2, 3].map((item) => (
        <div
          key={item}
          className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-3"
        >
          <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
          </div>
        </div>
      ))}
    </div>
  );
}

function CompactEmptyState({ icon: Icon, title, description, action = null }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Icon className="w-12 h-12 text-gray-300 mb-4" />
      <h3 className="text-lg font-medium text-gray-600 mb-2">{title}</h3>
      <p className="text-sm text-gray-400 max-w-xs">{description}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

function InlineMarkdownFragment({ text }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <>{children}</>,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

function StructuredAssistantMessage({ content, citations, onOpenPreview }) {
  const structuredContent = useMemo(
    () => buildStructuredMarkdownSections(applyInlineCitations(content, citations)),
    [citations, content],
  );

  const sections = structuredContent.sections?.length
    ? structuredContent.sections
    : (String(content || '').trim()
      ? [{
          heading: '',
          tone: 'default',
          blocks: [{ type: 'paragraph', text: String(content || '').trim() }],
        }]
      : []);

  if (!structuredContent.title && !sections.length) {
    return null;
  }

  return (
    <div
      className="markdown-content app-chat-structured"
      data-testid="assistant-structured-content"
      onClick={(event) => {
        const anchor = event.target.closest('a[href]');
        if (anchor) {
          event.preventDefault();
          onOpenPreview(anchor.href, anchor.textContent.trim());
        }
      }}
    >
      {structuredContent.title ? (
        <div className="app-chat-structured__title">{structuredContent.title}</div>
      ) : null}
      <div className="app-chat-structured__stack">
        {sections.map((section, sectionIndex) => (
          <section
            key={`${section.heading || 'section'}-${sectionIndex}`}
            className={`app-chat-structured-section app-chat-structured-section--${section.tone || 'default'}`}
          >
            {section.heading ? (
              <h3 className="app-chat-structured-section__heading">{section.heading}</h3>
            ) : null}
            <div className="app-chat-structured-section__body">
              {section.blocks.map((block, blockIndex) => {
                if (block.type === 'paragraph') {
                  return (
                    <div key={`paragraph-${blockIndex}`} className="app-chat-structured-paragraph">
                      <InlineMarkdownFragment text={block.text} />
                    </div>
                  );
                }

                if (block.type === 'list') {
                  return (
                    <ol
                      key={`list-${blockIndex}`}
                      className={`app-chat-structured-list ${block.ordered ? 'app-chat-structured-list--ordered' : 'app-chat-structured-list--unordered'}`}
                    >
                      {block.items.map((item, itemIndex) => (
                        <li key={`${item}-${itemIndex}`} className="app-chat-structured-list__item">
                          <span className="app-chat-structured-list__marker" aria-hidden="true">
                            {block.ordered ? itemIndex + 1 : ''}
                          </span>
                          <span className="app-chat-structured-list__content">
                            <InlineMarkdownFragment text={item} />
                          </span>
                        </li>
                      ))}
                    </ol>
                  );
                }

                if (block.type === 'code') {
                  return (
                    <pre key={`code-${blockIndex}`} className="app-chat-structured-code">
                      <code>{block.text}</code>
                    </pre>
                  );
                }

                return null;
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default function ChatPage() {
  const { chatId: urlChatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const { isMobile } = useSidebar();
  const { width: rightPanelWidth, startDrag: startRightDrag } = useResizablePanel(288, { min: 200, max: 520 });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('RESEARCH');
  const [court, setCourt] = useState('');
  const [matterContext, setMatterContext] = useState(null); // { id, name }
  const [isStreaming, setIsStreaming] = useState(false);
  const [chatId, setChatId] = useState(urlChatId || null);
  const [citations, setCitations] = useState([]);
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState('sources'); // 'sources' | 'history'
  const [docSources, setDocSources] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  // Linked doc state
  const [linkedDoc, setLinkedDoc] = useState(null); // { id, type, title }
  const [docHistoryChats, setDocHistoryChats] = useState([]);
  // Doc picker modal state
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState('documents');
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerDocs, setPickerDocs] = useState([]);
  const [pickerUploads, setPickerUploads] = useState([]);
  const [pickerSaved, setPickerSaved] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [workspace, setWorkspace] = useState(null);
  const [composerImprovement, setComposerImprovement] = useState(null);
  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
  const [activationHub, setActivationHub] = useState(null);
  const [journeyRoutes, setJourneyRoutes] = useState([]);
  const openPreview = useCallback((url, title = '') => { setPreviewUrl(url); setPreviewTitle(title); }, []);

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const shouldScrollOnNextRenderRef = useRef(false);
  const scrollIntervalRef = useRef(null);
  const messagesRef = useRef([]);
  const workspaceRef = useRef(null);
  const activeStreamRef = useRef(null);
  const streamCounterRef = useRef(0);
  const restoredDraftKeyRef = useRef(null);
  const lastUiRestoreKeyRef = useRef('');
  const pendingScrollRestoreRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const workspaceId = new URLSearchParams(location.search).get('workspace_id');
  const continuityKey = useMemo(() => buildChatContinuityKey({
    workspaceId: workspace?.workspace_id || workspaceId,
    chatId: chatId || urlChatId,
    matterId: matterContext?.id,
    linkedDocId: linkedDoc?.id,
  }), [chatId, linkedDoc?.id, matterContext?.id, urlChatId, workspace?.workspace_id, workspaceId]);
  const buildCurrentChatHref = useCallback(() => {
    const path = chatId || urlChatId ? `/chat/${chatId || urlChatId}` : '/chat';
    return `${path}${location.search || ''}`;
  }, [chatId, location.search, urlChatId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    document.title = 'Chat — Advoq';
  }, []);

  useEffect(() => {
    workspaceRef.current = workspace;
  }, [workspace]);

  useEffect(() => {
    if (!continuityKey || lastUiRestoreKeyRef.current === continuityKey) return;
    const snapshot = loadChatUiSnapshot(continuityKey);
    if (snapshot?.composer_improvement?.draft) {
      setComposerImprovement({
        open: Boolean(snapshot.composer_improvement.open),
        draft: snapshot.composer_improvement.draft || '',
        answer_goal: snapshot.composer_improvement.answer_goal || '',
        missing_facts_to_flag: Array.isArray(snapshot.composer_improvement.missing_facts_to_flag)
          ? snapshot.composer_improvement.missing_facts_to_flag
          : [],
      });
    } else {
      setComposerImprovement(null);
    }
    setShowRightPanel(Boolean(snapshot?.show_right_panel));
    setRightPanelTab(snapshot?.right_panel_tab === 'history' ? 'history' : 'sources');
    pendingScrollRestoreRef.current = typeof snapshot?.scroll_top === 'number' ? snapshot.scroll_top : null;
    setJourneyRoutes(listJourneyRoutes(continuityKey));
    lastUiRestoreKeyRef.current = continuityKey;
  }, [continuityKey]);

  useEffect(() => {
    if (!isMobile) return;
    setShowRightPanel(false);
    setSelectedCitation(null);
  }, [continuityKey, isMobile]);

  useEffect(() => {
    if (!continuityKey) return;
    recordJourneyVisit(continuityKey, {
      surface: 'CHAT',
      label: 'Chat',
      href: buildCurrentChatHref(),
    });
    setJourneyRoutes(listJourneyRoutes(continuityKey));
  }, [buildCurrentChatHref, continuityKey]);

  useEffect(() => {
    if (!continuityKey) return;
    saveChatUiSnapshot(continuityKey, {
      composerImprovement,
      messages,
    });
  }, [composerImprovement, continuityKey, messages]);

  useEffect(() => {
    if (!continuityKey) return;
    updateChatUiSnapshotMeta(continuityKey, {
      show_right_panel: showRightPanel,
      right_panel_tab: rightPanelTab,
    });
  }, [continuityKey, rightPanelTab, showRightPanel]);

  useEffect(() => () => {
    if (!continuityKey) return;
    updateChatUiSnapshotMeta(continuityKey, {
      scroll_top: scrollContainerRef.current?.scrollTop || 0,
      show_right_panel: showRightPanel,
      right_panel_tab: rightPanelTab,
    });
  }, [continuityKey, rightPanelTab, showRightPanel]);

  const buildWorkspaceQueryString = useCallback((overrides = {}) => {
    const params = new URLSearchParams(location.search);
    const linkedDocId = overrides.linkedDocId ?? linkedDoc?.id;
    const linkedDocType = overrides.linkedDocType ?? linkedDoc?.type;
    const linkedDocTitle = overrides.linkedDocTitle ?? linkedDoc?.title;
    const matterId = overrides.matterId ?? matterContext?.id;
    const matterName = overrides.matterName ?? matterContext?.name;
    const nextWorkspaceId = overrides.workspaceId ?? workspace?.workspace_id ?? workspaceId;
    const prefillQuery = overrides.prefillQuery;
    const prefillType = overrides.prefillType;

    if (nextWorkspaceId) params.set('workspace_id', nextWorkspaceId);
    else params.delete('workspace_id');

    if (linkedDocId && linkedDocType) {
      params.set('linked_doc_id', linkedDocId);
      params.set('linked_doc_type', linkedDocType);
      params.set('linked_doc_title', linkedDocTitle || 'Document');
    } else {
      params.delete('linked_doc_id');
      params.delete('linked_doc_type');
      params.delete('linked_doc_title');
    }

    if (matterId) {
      params.set('matter_id', matterId);
      params.set('matter_name', matterName || matterId);
    } else {
      params.delete('matter_id');
      params.delete('matter_name');
    }

    if (prefillQuery) params.set('prefill_query', prefillQuery);
    else if (!overrides.keepExistingPrefill || overrides.clearPrefill) params.delete('prefill_query');

    if (prefillType) params.set('prefill_type', prefillType);
    else if (!overrides.keepExistingPrefill || overrides.clearPrefill) params.delete('prefill_type');

    return params.toString();
  }, [linkedDoc, location.search, matterContext, workspace?.workspace_id, workspaceId]);

  const appendDictatedText = useCallback((text) => {
    const cleaned = String(text || '').trim();
    if (!cleaned) return;
    setInput(prev => {
      const spacer = prev && !/[ \n]$/.test(prev) ? ' ' : '';
      return `${prev}${spacer}${cleaned}`.trimStart();
    });
    textareaRef.current?.focus();
  }, []);

  const applyImprovedDraft = useCallback((draft, { successMessage = 'Sharper brief moved into the composer.' } = {}) => {
    const refined = String(draft || '').trim();
    if (!refined) return;
    setInput(refined);
    textareaRef.current?.focus();
    toast.success(successMessage);
  }, []);

  // Scroll to bottom on demand
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    userScrolledUpRef.current = false;
    setShowScrollButton(false);
  }, []);

  // Detect if user scrolled up — show scroll button if they're not at the bottom
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const scrolledUp = distFromBottom > 120;
    userScrolledUpRef.current = scrolledUp;
    setShowScrollButton(scrolledUp);
    if (continuityKey) {
      updateChatUiSnapshotMeta(continuityKey, { scroll_top: el.scrollTop });
    }
  }, [continuityKey]);

  // Auto-scroll on new message send
  useEffect(() => {
    if (shouldScrollOnNextRenderRef.current) {
      shouldScrollOnNextRenderRef.current = false;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (!isStreaming && !userScrolledUpRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isStreaming]);

  useEffect(() => {
    const nextScrollTop = pendingScrollRestoreRef.current;
    const el = scrollContainerRef.current;
    if (nextScrollTop == null || !el) return undefined;
    const rafId = window.requestAnimationFrame(() => {
      el.scrollTop = nextScrollTop;
      const distFromBottom = el.scrollHeight - nextScrollTop - el.clientHeight;
      const scrolledUp = distFromBottom > 120;
      userScrolledUpRef.current = scrolledUp;
      setShowScrollButton(scrolledUp);
      pendingScrollRestoreRef.current = null;
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [continuityKey, messages.length, showRightPanel]);

  // During streaming: follow the bottom if user hasn't scrolled up
  useEffect(() => {
    if (!isStreaming) {
      clearInterval(scrollIntervalRef.current);
      return;
    }
    scrollIntervalRef.current = setInterval(() => {
      if (userScrolledUpRef.current) return;
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 80);
    return () => clearInterval(scrollIntervalRef.current);
  }, [isStreaming]);

  // Persist unsent input to localStorage as a draft
  const chatIdForDraftRef = useRef(urlChatId || 'new');
  useEffect(() => { chatIdForDraftRef.current = chatId || urlChatId || 'new'; }, [chatId, urlChatId]);
  useEffect(() => {
    const key = `chat_input_draft_${chatIdForDraftRef.current}`;
    if (input) {
      localStorage.setItem(key, input);
    } else {
      localStorage.removeItem(key);
    }
  }, [input]);

  // Read linked doc from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const docId = params.get('linked_doc_id');
    const docType = params.get('linked_doc_type');
    const docTitle = params.get('linked_doc_title');
    if (docId && docType) {
      setLinkedDoc({ id: docId, type: docType, title: decodeURIComponent(docTitle || 'Document') });
    }
    const matterId = params.get('matter_id');
    const matterName = params.get('matter_name');
    if (matterId) {
      setMatterContext({ id: matterId, name: decodeURIComponent(matterName || matterId) });
    }
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prefillQuery = params.get('prefill_query');
    const starterMode = params.get('starter_mode');
    if (!urlChatId && !messages.length && !input && prefillQuery) {
      setInput(prefillQuery);
    }
    if (starterMode && MODES.some((item) => item.value === starterMode)) {
      setMode(starterMode);
    }
  }, [input, location.search, messages.length, urlChatId]);

  useEffect(() => {
    let cancelled = false;

    async function loadActivationHub() {
      try {
        const resp = await fetch(`${API}/activation-hub`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!resp.ok) return;
        const payload = await resp.json();
        if (!cancelled) setActivationHub(payload);
      } catch {}
    }

    if (user?.user_id) {
      loadActivationHub();
    }

    return () => {
      cancelled = true;
    };
  }, [getAuthHeaders, user?.user_id, urlChatId, messages.length]);

  const loadChat = useCallback(async (id) => {
    try {
      const resp = await fetch(`${API}/chats/${id}`, { headers: getAuthHeaders(), credentials: 'include' });
      if (resp.ok) {
        const chat = await resp.json();
        setChatId(id);
        setMode(chat.mode || 'RESEARCH');
        setMatterContext(chat.matter_id ? { id: chat.matter_id, name: chat.matter_name || chat.matter_id } : null);
        setLinkedDoc(chat.linked_doc_id && chat.linked_doc_type
          ? {
              id: chat.linked_doc_id,
              type: chat.linked_doc_type,
              title: chat.linked_doc_title || 'Document',
            }
          : null);
        const msgs = (chat.messages || []).map(m => ({
          localId: m.message_id || `${m.role || 'message'}-${Math.random().toString(36).slice(2, 8)}`,
          role: m.role === 'USER' ? 'user' : 'assistant',
          content: m.content,
          citations: m.citations || [],
          doc_sources: m.doc_sources || [],
          knowledge_sources: m.knowledge_sources || [],
          context_window: m.context_window || [],
          message_id: m.message_id,
        }));
        const baseKey = buildChatContinuityKey({
          workspaceId: chat.workspace_id || workspaceId,
          chatId: chat.chat_id || id,
          matterId: chat.matter_id,
          linkedDocId: chat.linked_doc_id,
        });
        const restoredMsgs = restoreChatMessagesWithUiState(msgs, loadChatUiSnapshot(baseKey));
        const enrichedMsgs = restoredMsgs.map((message, index, list) => {
          if (message.role !== 'assistant' || !String(message.content || '').trim()) return message;
          const pairedUser = [...list.slice(0, index)].reverse().find((item) => item.role === 'user');
          const existingPlan = Array.isArray(message.workflow_plan) ? message.workflow_plan : [];
          if (existingPlan.length) return message;
          const workflowPlan = buildChatWorkflowPlan({
            userPrompt: pairedUser?.content || '',
            assistantResponse: message.content,
            mode: chat.mode || 'RESEARCH',
            workspace: workspaceRef.current,
          });
          return {
            ...message,
            source_prompt: pairedUser?.content || message.source_prompt || '',
            source_mode: chat.mode || 'RESEARCH',
            response_refinement: message.response_refinement || {
              open: false,
              feedback: '',
              draft: '',
              answer_goal: '',
              missing_facts_to_flag: [],
              loading: false,
            },
            workflow_plan: workflowPlan.steps,
            workflow_summary: workflowPlan.summary,
            workflow_frame: workflowPlan.frame_label,
            task_shape: workflowPlan.task_shape,
          };
        });
        setMessages(enrichedMsgs);
        setCitations(enrichedMsgs.flatMap(m => m.citations || []));
        setDocSources(enrichedMsgs.flatMap(m => m.doc_sources || []));
        userScrolledUpRef.current = false;
        setTimeout(() => messagesEndRef.current?.scrollIntoView(), 100);
      }
    } catch {}
  }, [getAuthHeaders, workspaceId]);

  const loadWorkspace = useCallback(async (targetWorkspaceId) => {
    if (!targetWorkspaceId) {
      setWorkspace(null);
      return null;
    }
    try {
      const nextWorkspace = await fetchAgentWorkspace({
        workspaceId: targetWorkspaceId,
        headers: getAuthHeaders(),
      });
      setWorkspace(nextWorkspace);
      if (!linkedDoc && nextWorkspace.linked_doc) {
        setLinkedDoc(nextWorkspace.linked_doc);
      }
      if (!matterContext && nextWorkspace.matter_context) {
        setMatterContext(nextWorkspace.matter_context);
      }
      if (!urlChatId && !chatId && nextWorkspace.current_chat_id) {
        setChatId(nextWorkspace.current_chat_id);
        loadChat(nextWorkspace.current_chat_id);
        const query = buildWorkspaceQueryString({ workspaceId: nextWorkspace.workspace_id });
        window.history.replaceState(null, '', `/chat/${nextWorkspace.current_chat_id}${query ? `?${query}` : ''}`);
      }
      return nextWorkspace;
    } catch {
      return null;
    }
  }, [buildWorkspaceQueryString, chatId, getAuthHeaders, linkedDoc, loadChat, matterContext, urlChatId]);

  useEffect(() => {
    if (!workspaceId) {
      if (workspace !== null) {
        setWorkspace(null);
      }
      return;
    }
    loadWorkspace(workspaceId);
  }, [loadWorkspace, workspace, workspaceId]);

  const loadDocHistory = useCallback(async (docId) => {
    try {
      const resp = await fetch(`${API}/chats?linked_doc_id=${docId}`, { headers: getAuthHeaders(), credentials: 'include' });
      if (resp.ok) setDocHistoryChats(await resp.json());
    } catch {}
  }, [getAuthHeaders]);

  // Load existing chat + restore unsent input draft from localStorage
  useEffect(() => {
    if (urlChatId) {
      loadChat(urlChatId);
    } else {
      setMessages(prev => (prev.length ? [] : prev));
      setChatId(prev => (prev ? null : prev));
      setCitations(prev => (prev.length ? [] : prev));
    }
    const draftKey = `chat_input_draft_${urlChatId || 'new'}`;
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft && restoredDraftKeyRef.current !== draftKey) {
      restoredDraftKeyRef.current = draftKey;
      setInput(savedDraft);
      toast.info('Unsent message restored', { description: 'You had unsent text from your last session' });
    }
    if (!savedDraft) {
      restoredDraftKeyRef.current = draftKey;
    }
  }, [loadChat, urlChatId]);

  // Load doc history when linkedDoc changes
  useEffect(() => {
    if (linkedDoc?.id) {
      loadDocHistory(linkedDoc.id);
    }
  }, [linkedDoc?.id, loadDocHistory]);

  useEffect(() => {
    setMessages((current) => {
      let didChange = false;
      const next = current.map((message, index, list) => {
        if (message.role !== 'assistant') return message;
        const pairedUser = [...list.slice(0, index)].reverse().find((item) => item.role === 'user');
        const responseRefinement = message.response_refinement || {
          open: false,
          feedback: '',
          draft: '',
          answer_goal: '',
          missing_facts_to_flag: [],
          loading: false,
        };
        const workflowPlan = Array.isArray(message.workflow_plan) && message.workflow_plan.length
          ? { steps: message.workflow_plan, summary: message.workflow_summary, frame_label: message.workflow_frame, task_shape: message.task_shape }
          : (String(message.content || '').trim()
            ? buildChatWorkflowPlan({
                userPrompt: pairedUser?.content || message.source_prompt || '',
                assistantResponse: message.content,
                mode: message.source_mode || mode,
                workspace,
              })
            : null);

        const nextMessage = {
          ...message,
          source_prompt: message.source_prompt || pairedUser?.content || '',
          source_mode: message.source_mode || mode,
          response_refinement: responseRefinement,
          ...(workflowPlan ? {
            workflow_plan: workflowPlan.steps,
            workflow_summary: workflowPlan.summary,
            workflow_frame: workflowPlan.frame_label,
            task_shape: workflowPlan.task_shape,
          } : {}),
        };
        if (nextMessage === message) return message;
        const changed = (
          nextMessage.source_prompt !== message.source_prompt
          || nextMessage.source_mode !== message.source_mode
          || nextMessage.response_refinement !== message.response_refinement
          || nextMessage.workflow_plan !== message.workflow_plan
        );
        if (changed) didChange = true;
        return changed ? nextMessage : message;
      });
      if (!didChange) return current;
      messagesRef.current = next;
      return next;
    });
  }, [mode, workspace]);

  const ensureWorkspaceForMessage = useCallback(async (seedText) => {
    try {
      const ensured = await ensureAgentWorkspace({
        headers: getAuthHeaders(),
        payload: {
          source_agent: 'CHAT_AGENT',
          seed_text: seedText,
          workspace_id: workspace?.workspace_id || workspaceId || null,
          linked_doc_id: linkedDoc?.id || null,
          linked_doc_type: linkedDoc?.type || null,
          linked_doc_title: linkedDoc?.title || null,
          matter_id: matterContext?.id || null,
          matter_name: matterContext?.name || null,
        },
      });
      setWorkspace(ensured);
      const query = buildWorkspaceQueryString({ workspaceId: ensured.workspace_id });
      const path = urlChatId ? `/chat/${urlChatId}` : (chatId ? `/chat/${chatId}` : '/chat');
      window.history.replaceState(null, '', `${path}${query ? `?${query}` : ''}`);
      return ensured;
    } catch {
      toast.error('Failed to open shared workspace');
      return null;
    }
  }, [buildWorkspaceQueryString, chatId, getAuthHeaders, linkedDoc, matterContext, urlChatId, workspace?.workspace_id, workspaceId]);

  const openDocPicker = async () => {
    setShowDocPicker(true);
    setPickerLoading(true);
    try {
      const [docsResp, uploadsResp, savedResp] = await Promise.all([
        fetch(`${API}/documents`, { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API}/uploads`, { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(`${API}/saved`, { headers: getAuthHeaders(), credentials: 'include' }),
      ]);
      if (docsResp.ok) setPickerDocs(await docsResp.json());
      if (uploadsResp.ok) setPickerUploads(await uploadsResp.json());
      if (savedResp.ok) setPickerSaved((await savedResp.json()).filter(s => ['DOCUMENT', 'RESEARCH', 'CASE_LAW'].includes(s.type)));
    } catch {}
    finally { setPickerLoading(false); }
  };

  const selectDocFromPicker = (docId, docType, docTitle) => {
    setLinkedDoc({ id: docId, type: docType, title: docTitle });
    setShowDocPicker(false);
    // update URL to reflect linked doc
    const params = new URLSearchParams(location.search);
    params.set('linked_doc_id', docId);
    params.set('linked_doc_type', docType);
    params.set('linked_doc_title', encodeURIComponent(docTitle));
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    toast.success(`"${docTitle}" linked — it will be referenced in all messages`);
    loadDocHistory(docId);
  };

  const clearLinkedDoc = () => {
    setLinkedDoc(null);
    setDocHistoryChats([]);
    const params = new URLSearchParams(location.search);
    params.delete('linked_doc_id');
    params.delete('linked_doc_type');
    params.delete('linked_doc_title');
    const newSearch = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`);
  };

  const startNewDocChat = () => {
    if (!linkedDoc) return;
    const params = new URLSearchParams();
    params.set('linked_doc_id', linkedDoc.id);
    params.set('linked_doc_type', linkedDoc.type);
    params.set('linked_doc_title', encodeURIComponent(linkedDoc.title));
    setChatId(null);
    setMessages([]);
    setCitations([]);
    setDocSources([]);
    setSelectedCitation(null);
    setInput('');
    setComposerImprovement(null);
    setPendingFiles([]);
    navigate(`/chat?${params.toString()}`, { replace: true });
  };

  const handleNewChat = () => {
    setChatId(null);
    setMessages([]);
    setCitations([]);
    setDocSources([]);
    setSelectedCitation(null);
    setShowRightPanel(false);
    setInput('');
    setComposerImprovement(null);
    setPendingFiles([]);
    setLinkedDoc(null);
    setMatterContext(null);
    setDocHistoryChats([]);
    navigate('/chat', { replace: true });
  };

  const moveChatToTrash = async (targetChatId, options = {}) => {
    if (!targetChatId) return;
    const { title = 'this chat', clearCurrent = false } = options;
    if (!window.confirm(`Move "${title}" to trash? You can permanently delete it later from Settings.`)) return;

    if (clearCurrent && isStreaming) handleStopGeneration();

    try {
      const resp = await fetch(`${API}/chats/${targetChatId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Delete failed');

      setDocHistoryChats(prev => prev.filter(chat => chat.chat_id !== targetChatId));

      if (clearCurrent) {
        const queryString = buildWorkspaceQueryString();
        setChatId(null);
        setMessages([]);
        setCitations([]);
        setDocSources([]);
        setSelectedCitation(null);
        setInput('');
        setComposerImprovement(null);
        setPendingFiles([]);
        setShowRightPanel(false);
        navigate(`/chat${queryString ? `?${queryString}` : ''}`, { replace: true });
      }

      toast.success(`"${title}" moved to trash`);
    } catch {
      toast.error('Failed to move chat to trash');
    }
  };

  const moveLinkedResourceToTrash = async () => {
    if (!linkedDoc || !['upload', 'saved_item'].includes(linkedDoc.type)) return;
    const endpoint = linkedDoc.type === 'upload'
      ? `${API}/uploads/${linkedDoc.id}`
      : `${API}/saved/${linkedDoc.id}`;
    const label = linkedDoc.title || 'this item';
    if (!window.confirm(`Move "${label}" to trash? You can permanently delete it later from Settings.`)) return;

    try {
      const resp = await fetch(endpoint, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (!resp.ok) throw new Error('Delete failed');

      if (linkedDoc.type === 'upload') {
        setPickerUploads(prev => prev.filter(item => item.upload_id !== linkedDoc.id));
      } else {
        setPickerSaved(prev => prev.filter(item => item.item_id !== linkedDoc.id));
      }
      clearLinkedDoc();
      toast.success(`"${label}" moved to trash`);
    } catch {
      toast.error('Failed to move item to trash');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    const allowed = new Set(['.pdf', '.docx', '.txt', '.doc']);
    const valid = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowed.has(ext)) { toast.error(`${file.name}: unsupported type`); return false; }
      if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name}: exceeds 15 MB`); return false; }
      return true;
    });
    if (valid.length) setPendingFiles(prev => [...prev, ...valid]);
  };

  const removePendingFile = useCallback((idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const uploadPendingFiles = useCallback(async () => {
    if (!pendingFiles.length) return;
    setIsUploading(true);
    const token = localStorage.getItem('session_token');
    try {
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await fetch(`${API}/uploads`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          body: formData,
        });
        if (resp.ok) {
          toast.success(`"${file.name}" indexed`);
        } else {
          const err = await resp.json().catch(() => ({}));
          toast.error(err.detail || `Upload failed: ${file.name}`);
        }
      }
      setPendingFiles([]);
    } catch { toast.error('Upload failed'); }
    finally { setIsUploading(false); }
  }, [pendingFiles]);

  const updateMessageByLocalId = useCallback((localId, updater) => {
    if (!localId) return;
    const next = updateMessageListByLocalId(messagesRef.current, localId, updater);
    if (next === messagesRef.current) return;
    messagesRef.current = next;
    setMessages(next);
  }, []);

  const updateAssistantRefinementState = useCallback((localId, patch) => {
    updateMessageByLocalId(localId, (message) => ({
      ...message,
      response_refinement: {
        open: Boolean(message.response_refinement?.open),
        feedback: '',
        draft: '',
        answer_goal: '',
        missing_facts_to_flag: [],
        loading: false,
        ...message.response_refinement,
        ...patch,
      },
    }));
  }, [updateMessageByLocalId]);

  const buildImprovePromptPayload = useCallback((draft, extra = {}) => ({
    draft,
    messages: messagesRef.current.slice(-6).map((message) => ({ role: message.role, content: message.content })),
    mode: extra.mode || mode,
    language: user?.language || 'ENGLISH',
    chat_id: chatId,
    court: court || null,
    matter_id: matterContext?.id || null,
    matter_name: matterContext?.name || null,
    workspace_id: workspace?.workspace_id || workspaceId || null,
    linked_doc_id: linkedDoc?.id || null,
    linked_doc_type: linkedDoc?.type || null,
    linked_doc_title: linkedDoc?.title || null,
    feedback: extra.feedback || null,
    assistant_response: extra.assistantResponse || null,
  }), [chatId, court, linkedDoc, matterContext, mode, user?.language, workspace?.workspace_id, workspaceId]);

  const requestImprovedBrief = useCallback(async (draft, extra = {}) => {
    const token = localStorage.getItem('session_token');
    const resp = await fetch(`${API}/chat/improve-prompt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify(buildImprovePromptPayload(draft, extra)),
    });
    if (!resp.ok) throw new Error('Improve failed');
    return resp.json();
  }, [buildImprovePromptPayload]);

  const resolveWorkflowDraft = useCallback((message) => {
    const refinementDraft = String(message?.response_refinement?.draft || '').trim();
    if (refinementDraft) return refinementDraft;
    return String(message?.source_prompt || '').trim() || String(message?.content || '').trim();
  }, []);

  const openWorkflowStep = useCallback((message, step, options = {}) => {
    if (continuityKey) {
      updateChatUiSnapshotMeta(continuityKey, {
        scroll_top: scrollContainerRef.current?.scrollTop || 0,
        show_right_panel: showRightPanel,
        right_panel_tab: rightPanelTab,
      });
    }
    const prefillQuery = String(options.prefillQuery || '').trim() || resolveWorkflowDraft(message);
    const destination = buildAgentDestination({
      recommendation: step,
      workspaceQueryString: buildWorkspaceQueryString({ clearPrefill: true }),
      workspace,
      matterId: matterContext?.id,
      matterName: matterContext?.name,
      prefillQuery,
      prefillType: step.prefillType || 'TOPIC',
      focusAuthority: step.focusAuthority,
      focusIssue: step.focusIssue,
      journeyKey: continuityKey,
      chatId: chatId || urlChatId || '',
      chatReturnPath: buildCurrentChatHref(),
    });
    recordJourneyVisit(continuityKey, {
      surface: step.target,
      label: step.agent,
      href: destination.href,
    });
    setJourneyRoutes(listJourneyRoutes(continuityKey));
    navigate(destination.href, destination.state ? { state: destination.state } : undefined);
  }, [buildCurrentChatHref, buildWorkspaceQueryString, chatId, continuityKey, matterContext?.id, matterContext?.name, navigate, resolveWorkflowDraft, rightPanelTab, showRightPanel, urlChatId, workspace]);

  const handleGenerateResponseRefinement = useCallback(async (message) => {
    const basePrompt = String(message?.source_prompt || '').trim();
    const feedback = String(message?.response_refinement?.feedback || '').trim();
    if (!message?.localId || !basePrompt || !feedback) return;
    updateAssistantRefinementState(message.localId, { loading: true, open: true });
    try {
      const suggestion = await requestImprovedBrief(basePrompt, {
        mode: message.source_mode || mode,
        feedback,
        assistantResponse: message.content,
      });
      if (!suggestion?.refined_prompt) throw new Error('Missing refined prompt');
      updateAssistantRefinementState(message.localId, {
        loading: false,
        open: true,
        draft: suggestion.refined_prompt,
        answer_goal: suggestion.answer_goal || '',
        missing_facts_to_flag: suggestion.missing_facts_to_flag || [],
      });
    } catch {
      updateAssistantRefinementState(message.localId, { loading: false });
      toast.error('Could not refine the next brief right now.');
    }
  }, [mode, requestImprovedBrief, updateAssistantRefinementState]);

  const stopActiveStream = useCallback(({ keepPartial = true, removeTurn = false } = {}) => {
    const active = activeStreamRef.current;
    if (!active) return null;
    activeStreamRef.current = null;
    if (active.controller) {
      active.controller.abort();
    }
    const baseMessages = removeActiveTurnMessages(messagesRef.current, active);
    if (removeTurn) {
      messagesRef.current = baseMessages;
      setMessages(baseMessages);
    } else {
      updateMessageByLocalId(active.assistantLocalId, (message) => ({
        ...message,
        isStreaming: false,
        toolStatus: null,
      }));
      if (!keepPartial) {
        updateMessageByLocalId(active.assistantLocalId, (message) => ({
          ...message,
          content: '',
          isStreaming: false,
          toolStatus: null,
        }));
      }
    }
    setIsStreaming(false);
    return { ...active, baseMessages };
  }, [updateMessageByLocalId]);

  const handleStopGeneration = useCallback(() => {
    stopActiveStream({ keepPartial: true, removeTurn: false });
  }, [stopActiveStream]);

  const handleImprovePrompt = useCallback(async () => {
    const draft = input.trim();
    if (!draft || isImprovingPrompt) return;
    setIsImprovingPrompt(true);
    try {
      const suggestion = await requestImprovedBrief(draft);
      const refined = suggestion?.refined_prompt?.trim();
      if (!refined) throw new Error('Missing refined prompt');
      setComposerImprovement({
        open: true,
        draft: refined,
        answer_goal: suggestion.answer_goal || '',
        missing_facts_to_flag: suggestion.missing_facts_to_flag || [],
      });
      toast.success('Improved brief ready to review.');
    } catch {
      toast.error('Could not improve the prompt right now.');
    } finally {
      setIsImprovingPrompt(false);
    }
  }, [input, isImprovingPrompt, requestImprovedBrief]);

  const handleSend = useCallback(async (text, options = {}) => {
    const messageText = String(typeof text === 'string' ? text : input).trim();
    if (!messageText) return;

    const nextMode = options.mode || mode;
    if (nextMode !== mode) setMode(nextMode);
    setComposerImprovement(null);

    let baseMessages = Array.isArray(options.baseMessages) ? options.baseMessages : messagesRef.current;
    let replaceMessageId = options.replaceMessageId || null;
    if (activeStreamRef.current) {
      if (messageText === activeStreamRef.current.submittedText?.trim()) return;
      const stopped = stopActiveStream({ keepPartial: false, removeTurn: true });
      baseMessages = stopped?.baseMessages || [];
      replaceMessageId = stopped?.userMessageId || replaceMessageId;
    }

    if (pendingFiles.length && !isUploading) {
      await uploadPendingFiles();
    }

    userScrolledUpRef.current = false;
    setShowScrollButton(false);
    shouldScrollOnNextRenderRef.current = true;

    const streamId = streamCounterRef.current + 1;
    streamCounterRef.current = streamId;
    const userLocalId = makeLocalMessageId('user');
    const assistantLocalId = makeLocalMessageId('assistant');
    const userMsg = { localId: userLocalId, role: 'user', content: messageText, message_id: replaceMessageId || null };
    const assistantMsg = {
      localId: assistantLocalId,
      role: 'assistant',
      content: '',
      citations: [],
      doc_sources: [],
      knowledge_sources: [],
      context_window: [],
      isStreaming: true,
      source_prompt: messageText,
      source_mode: nextMode,
      response_refinement: {
        open: false,
        feedback: '',
        draft: '',
        answer_goal: '',
        missing_facts_to_flag: [],
        loading: false,
      },
    };
    const nextMessages = [...baseMessages, userMsg, assistantMsg];
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
    setIsStreaming(true);
    activeStreamRef.current = {
      streamId,
      userLocalId,
      assistantLocalId,
      userMessageId: replaceMessageId || null,
      submittedText: messageText,
      controller: null,
    };

    const isCurrentStream = () => activeStreamRef.current?.streamId === streamId;

    const activeWorkspace = await ensureWorkspaceForMessage(messageText);
    if (!isCurrentStream()) return;

    const controller = new AbortController();
    activeStreamRef.current = {
      ...activeStreamRef.current,
      controller,
    };

    try {
      const allMsgs = [...baseMessages, userMsg].map((message) => ({ role: message.role, content: message.content }));
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({
          messages: allMsgs,
          mode: nextMode,
          language: user?.language || 'ENGLISH',
          chat_id: chatId,
          replace_message_id: replaceMessageId,
          court: court || null,
          matter_id: matterContext?.id || null,
          matter_name: matterContext?.name || null,
          workspace_id: activeWorkspace?.workspace_id || workspace?.workspace_id || workspaceId || null,
          linked_doc_id: linkedDoc?.id || null,
          linked_doc_type: linkedDoc?.type || null,
          linked_doc_title: linkedDoc?.title || null,
        }),
        signal: controller.signal,
      });

      if (!resp.ok) throw new Error('Stream failed');
      let fullText = '';
      await readEventStream(resp, async (data) => {
        if (!isCurrentStream()) return;

        if (data.type === 'chat_id' && data.chat_id) {
          setChatId(data.chat_id);
          if (!urlChatId) {
            const params = new URLSearchParams(location.search);
            const suffix = params.toString();
            window.history.replaceState(null, '', `/chat/${data.chat_id}${suffix ? `?${suffix}` : ''}`);
          }
        }
        if (data.type === 'user_message_id' && data.message_id) {
          activeStreamRef.current = {
            ...activeStreamRef.current,
            userMessageId: data.message_id,
          };
          updateMessageByLocalId(userLocalId, (message) => ({ ...message, message_id: data.message_id }));
        }
        if (data.type === 'assistant_message_id' && data.message_id) {
          updateMessageByLocalId(assistantLocalId, (message) => ({ ...message, message_id: data.message_id }));
        }
        if (data.type === 'tool_call') {
          const toolLabels = {
            search_indian_kanoon: 'Searching Indian Kanoon…',
            fetch_matter_documents: 'Retrieving matter documents…',
            get_statute_section: 'Looking up statute section…',
          };
          const statusText = toolLabels[data.tool] || `Running tool: ${data.tool}…`;
          updateMessageByLocalId(assistantLocalId, (message) => ({ ...message, toolStatus: statusText }));
        }
        if (data.type === 'text') {
          fullText += data.text;
          const provisionalCitations = extractCitationsClient(fullText);
          updateMessageByLocalId(assistantLocalId, (message) => ({
            ...message,
            content: fullText,
            citations: provisionalCitations,
            toolStatus: null,
          }));
        }
        if (data.type === 'citations') {
          const nextCitations = data.citations || [];
          setCitations((prev) => [...prev, ...nextCitations]);
          updateMessageByLocalId(assistantLocalId, (message) => ({ ...message, citations: nextCitations }));
        }
        if (data.type === 'doc_sources') {
          const sources = data.sources || [];
          setDocSources((prev) => [...prev, ...sources]);
          updateMessageByLocalId(assistantLocalId, (message) => ({ ...message, doc_sources: sources }));
        }
        if (data.type === 'knowledge_sources') {
          const sources = data.sources || [];
          updateMessageByLocalId(assistantLocalId, (message) => ({ ...message, knowledge_sources: sources }));
        }
        if (data.type === 'context_window') {
          const contextWindow = data.items || [];
          updateMessageByLocalId(assistantLocalId, (message) => ({ ...message, context_window: contextWindow }));
        }
        if (data.type === 'done') {
          const activeWorkspaceId = activeWorkspace?.workspace_id || workspace?.workspace_id || workspaceId;
          let refreshedWorkspace = workspace;
          if (activeWorkspaceId) {
            refreshedWorkspace = await loadWorkspace(activeWorkspaceId);
          }
          if (!isCurrentStream()) return;
          const workflowPlan = buildChatWorkflowPlan({
            userPrompt: messageText,
            assistantResponse: fullText,
            mode: nextMode,
            workspace: refreshedWorkspace,
          });
          updateMessageByLocalId(assistantLocalId, (message) => ({
            ...message,
            content: finalizeAssistantContent(fullText, message.content),
            isStreaming: false,
            workflow_plan: workflowPlan.steps,
            workflow_summary: workflowPlan.summary,
            workflow_frame: workflowPlan.frame_label,
            task_shape: workflowPlan.task_shape,
            toolStatus: null,
          }));
          setInput((current) => (current.trim() === messageText ? '' : current));
        }
        if (data.type === 'error') {
          const errorMessage = data.error || 'AI service temporarily unavailable.';
          toast.error(errorMessage);
          updateMessageByLocalId(assistantLocalId, (message) => ({
            ...message,
            content: finalizeAssistantContent(fullText, message.content),
            isStreaming: false,
            toolStatus: null,
          }));
        }
      });
    } catch (err) {
      if (!isCurrentStream()) return;
      if (err.name !== 'AbortError') {
        toast.error('Failed to get response. Please try again.');
        updateMessageByLocalId(assistantLocalId, (message) => ({
          ...message,
          content: finalizeAssistantContent('', message.content),
          isStreaming: false,
          toolStatus: null,
        }));
      }
    } finally {
      if (isCurrentStream()) {
        activeStreamRef.current = null;
        setIsStreaming(false);
      }
    }
  }, [chatId, court, ensureWorkspaceForMessage, input, isUploading, linkedDoc, loadWorkspace, location.search, matterContext, mode, pendingFiles.length, stopActiveStream, uploadPendingFiles, updateMessageByLocalId, urlChatId, user?.language, workspace, workspaceId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (activeStreamRef.current && input.trim() === activeStreamRef.current.submittedText?.trim()) return;
      handleSend();
    }
  };

  const handleCopy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); };

  const handleFeedback = async (messageId, score) => {
    try {
      await fetch(`${API}/feedback`, { method: 'POST', headers: getAuthHeaders(), credentials: 'include', body: JSON.stringify({ message_id: messageId, score }) });
      toast.success(score > 0 ? 'Thanks!' : 'Feedback noted');
    } catch {}
  };

  const isEmpty = messages.length === 0;
  const greeting = new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening';
  const firstName = user?.name?.trim()?.split(/\s+/)[0] || 'there';
  const activeMode = MODES.find(item => item.value === mode) || MODES[0];
  const continueItems = activationHub?.continue_items || [];
  const starterCards = activationHub?.starter_workflows?.length
    ? activationHub.starter_workflows
    : WORKFLOW_STARTS.map((card) => ({ ...card, workflow_id: card.title, label: card.title }));
  const reentryPrompt = activationHub?.reentry_prompt || null;
  const verifiedCitationCount = citations.filter(citation => citation.verified).length;
  const knowledgeSourcesPanel = useMemo(() => {
    const combined = [];
    messages.forEach((message) => {
      if (message.role !== 'assistant' || !Array.isArray(message.knowledge_sources)) return;
      combined.push(...message.knowledge_sources);
    });
    const deduped = dedupeSourceList(combined) || [];
    return deduped.slice(0, 12);
  }, [messages]);
  const trustSummary = useMemo(
    () => buildTrustSummary(knowledgeSourcesPanel),
    [knowledgeSourcesPanel],
  );
  const sourceTrustHeadline = trustSummary.length > 0
    ? trustSummary.slice(0, 2).map((item) => `${item.count} ${item.shortLabel.toLowerCase()}`).join(' · ')
    : 'Indian Kanoon first';
  const composerStatus = matterContext
    ? `Matter live: ${matterContext.name}`
    : linkedDoc
      ? `Linked file: ${linkedDoc.title}`
      : workspace
        ? `Shared desk live: ${workspace.title}`
        : 'Chat is the front door. Add facts, files, or voice and Advoq will keep the matter warm.';
  const sourcesSummary = verifiedCitationCount || docSources.length || knowledgeSourcesPanel.length
    ? [
        verifiedCitationCount ? `${verifiedCitationCount} verified citations` : null,
        sourceTrustHeadline,
      ].filter(Boolean).join(' · ')
    : 'Indian Kanoon core · trust labels stay visible';
  const activeSubmittedText = activeStreamRef.current?.submittedText?.trim() || '';
  const canRestartStream = canRestartFromDraft({ isStreaming, draft: input, submittedText: activeSubmittedText });
  const workspaceLabel = workspace?.title || matterContext?.name || linkedDoc?.title || 'No matter linked yet';
  const composerHint = isStreaming
    ? (canRestartStream ? 'Press Enter to restart from the edited draft' : 'The answer is still streaming. Edit the draft anytime.')
    : `${sourcesSummary} · Enter to send`;

  const openContinueItem = useCallback((item) => {
    if (!item?.href) return;
    navigate(item.href);
  }, [navigate]);

  const renderWorkflowIcon = useCallback((target, size = 13) => {
    if (target === 'SABHA') return <Users size={size} />;
    if (target === 'AINA') return <Eye size={size} />;
    if (target === 'JOLLY') return <Mic size={size} />;
    if (target === 'VAKALAT') return <FileText size={size} />;
    if (target === 'RESEARCH_AGENT') return <BookOpen size={size} />;
    return <MessageSquare size={size} />;
  }, []);

  const runStarterWorkflow = useCallback((workflow) => {
    if (!workflow) return;
    if (workflow.kind === 'JOLLY') {
      navigate(workflow.path || '/jolly');
      return;
    }
    handleSend(workflow.prompt, { mode: workflow.mode || mode });
  }, [handleSend, mode, navigate]);

  const toggleSourcesPanel = useCallback(() => {
    setSelectedCitation(null);
    if (showRightPanel && rightPanelTab === 'sources') {
      setShowRightPanel(false);
      return;
    }
    setRightPanelTab('sources');
    setShowRightPanel(true);
  }, [rightPanelTab, showRightPanel]);

  return (
    <div style={{ display: 'contents' }}>
      
      <main className="app-main" data-testid="chat-page" data-chat-mode={mode}>
        {/* Chat header bar */}
        <div className="app-chat-header shrink-0 !pt-0">
          <div className="app-chat-header__row">
            <button data-testid="new-chat-header-btn" onClick={handleNewChat} className="app-chat-button app-chat-button--new">
              <Plus size={14} /> New
            </button>
            {chatId && (
              <button
                onClick={() => moveChatToTrash(chatId, {
                  title: messages[0]?.content?.slice(0, 40) || 'Current chat',
                  clearCurrent: true,
                })}
                className="app-chat-button"
                title="Move current chat to trash"
              >
                <Trash2 size={14} /> Trash
              </button>
            )}
            <FeatureHistoryMenu feature="chat" matterId={matterContext?.id || ''} workspaceId={workspace?.workspace_id || workspaceId || ''} label="Recent Chats" dropdownAlign="left" />
            <div className="app-chat-toolbar">
              <div className="app-chat-toolbar__group">
                <div className="app-chat-toolbar__mode-summary" data-testid="active-mode-summary">
                  <span className="app-chat-toolbar__mode-badge">
                    <activeMode.icon size={13} />
                    {activeMode.label}
                  </span>
                  <span className="app-chat-toolbar__mode-copy">{activeMode.desc}</span>
                </div>
              </div>
              <div className="app-chat-toolbar__group ml-auto">
                <select
                  data-testid="mode-selector"
                  value={mode}
                  onChange={e => setMode(e.target.value)}
                  className="advoq-input app-chat-select text-sm"
                >
                  {MODES.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
                <select data-testid="court-selector" value={court} onChange={e => setCourt(e.target.value)}
                  className="advoq-input app-chat-select text-sm">
                  <option value="">General</option>
                  <option value="Supreme Court of India">Supreme Court</option>
                  <option value="Delhi High Court">Delhi HC</option>
                  <option value="Bombay High Court">Bombay HC</option>
                  <option value="Madras High Court">Madras HC</option>
                  <option value="Calcutta High Court">Calcutta HC</option>
                  <option value="Karnataka High Court">Karnataka HC</option>
                </select>
                <button
                  onClick={openDocPicker}
                  className={`app-chat-button ${linkedDoc ? 'is-active' : ''}`}
                  title="Open saved document"
                >
                  <FolderOpen size={14} /> Docs
                </button>
                {matterContext && (
                  <button
                    className="app-chat-button is-active"
                    title={`Matter context: ${matterContext.name}`}
                  >
                    <Users size={14} /> {matterContext.name}
                  </button>
                )}
                {workspace && (
                  <>
                    <button
                      className="app-chat-button is-active"
                      title={`Workspace: ${workspace.title}`}
                    >
                      <MessageSquare size={14} /> {workspace.activity_type?.replace(/_/g, ' ') || 'Workspace'}
                    </button>
                    <button
                      onClick={() => navigate(`/research?${buildWorkspaceQueryString({ workspaceId: workspace.workspace_id })}`)}
                      className="app-chat-button"
                      title="Switch to Research Agent"
                    >
                      <BookOpen size={14} /> Research Agent
                    </button>
                    {workspace.master_document_id && (
                      <button
                        onClick={() => navigate(`/documents/${workspace.master_document_id}`)}
                        className="app-chat-button"
                        title="Open workspace master document"
                      >
                        <FileText size={14} /> Master Doc
                      </button>
                    )}
                  </>
                )}
                <button
                  data-testid="toggle-citations-panel"
                  onClick={toggleSourcesPanel}
                  className="app-chat-button"
                  title={showRightPanel && rightPanelTab === 'sources' ? 'Hide sources panel' : 'Show sources panel'}
                >
                  {showRightPanel && rightPanelTab === 'sources' ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
                  Sources
                </button>
              </div>
            </div>
          </div>
          <div className="app-chat-header__meta" style={{ paddingTop: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span className="app-chat-button is-active" style={{ pointerEvents: 'none' }}>
                <MessageSquare size={13} /> {workspaceLabel}
              </span>
              {journeyRoutes
                .filter((route) => route.surface !== 'CHAT')
                .slice(0, 4)
                .map((route) => (
                  <button
                    key={`${route.surface}-${route.href}`}
                    type="button"
                    className="app-chat-button"
                    data-testid={`journey-route-${route.surface}`}
                    onClick={() => navigate(route.href)}
                    title={`Return to ${route.label}`}
                  >
                    <History size={13} /> {route.label}
                  </button>
                ))}
            </div>
          </div>
        </div>

        <div className="app-chat-body flex flex-1 min-h-0">
          {/* Messages area */}
          <div className="flex-1 flex flex-col min-w-0">
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className={`app-chat-scroll-region flex-1 overflow-y-auto px-3 py-4 md:px-4 md:py-6 ${isEmpty ? 'app-chat-scroll-region--empty' : ''}`}
            >
              {isEmpty ? (
                <div className="app-chat-empty" data-testid="chat-empty-state" style={{ maxWidth: '980px', margin: '0 auto', paddingTop: '24px' }}>
                  <div className="app-chat-empty__panel">
                    <div className="app-chat-empty__intro">
                      <p className="app-chat-empty__eyebrow">{activeMode.label}</p>
                      <h1 className="app-chat-empty__title">
                        Good {greeting},&nbsp;<span className="app-chat-greeting-name">{firstName}</span>
                      </h1>
                      <p className="app-chat-empty__subtitle">
                        Start with the live issue, the next filing step, or the exact question you want grounded in law.
                      </p>
                    </div>
                    <div className="app-chat-pill-list" style={{ justifyContent: 'center' }}>
                      {(reentryPrompt?.prompt ? [reentryPrompt.prompt, ...SAMPLE_QUESTIONS.slice(0, 2)] : SAMPLE_QUESTIONS).map((q, i) => (
                        <button key={i} data-testid={`sample-question-top-${i}`} onClick={() => handleSend(q)} className="app-chat-pill">
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>

                  {continueItems.length ? (
                    <div data-testid="continue-rail" className="app-chat-continuity-rail">
                      {continueItems.slice(0, 3).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          className="app-chat-continuity-link"
                          onClick={() => openContinueItem(item)}
                        >
                          {item.eyebrow ? <span className="app-chat-continuity-link__eyebrow">{item.eyebrow}</span> : null}
                          <span className="app-chat-continuity-link__title">{item.title}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="app-chat-thread mx-auto space-y-4 md:space-y-5">
                  {messages.map((msg, idx) => (
                    <div key={msg.localId || idx} className={`app-chat-message gap-2 md:gap-3 ${msg.role === 'user' ? 'app-chat-message--user' : 'app-chat-message--assistant'}`} data-testid={`message-${idx}`}>
                      {msg.role === 'assistant' && (
                        <div className="app-chat-avatar app-chat-avatar--brand" data-testid="assistant-brand-avatar">
                          <BrandLogo variant="mark" alt="Advoq" className="app-chat-avatar__mark" />
                        </div>
                      )}
                      <div className={msg.role === 'user' ? 'w-full max-w-full md:max-w-[78%]' : 'flex-1 min-w-0 w-full'}>
                        {msg.role === 'user' ? (
                          <div className="app-chat-bubble app-chat-bubble--user break-words">
                            <p className="text-sm whitespace-pre-wrap break-words" style={{ fontFamily: 'var(--font-ui)', color: 'var(--text-primary)' }}>{msg.content}</p>
                          </div>
                        ) : (
                          <div
                            className="text-sm app-chat-bubble app-chat-bubble--assistant min-w-0 break-words"
                            style={{ fontFamily: 'var(--font-ui)', color: 'var(--text-primary)' }}
                          >
                            <StructuredAssistantMessage
                              content={msg.content}
                              citations={msg.citations}
                              onOpenPreview={openPreview}
                            />
                            {msg.isStreaming && msg.content && (
                              <span className="app-chat-streaming-cursor" aria-hidden="true" />
                            )}
                            {msg.isStreaming && !msg.content && !msg.toolStatus && (
                              <span className="inline-flex gap-1 mt-1">
                                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                              </span>
                            )}
                            {msg.isStreaming && !msg.content && msg.toolStatus && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                                <span>{msg.toolStatus}</span>
                              </span>
                            )}
                          </div>
                        )}

                        {/* Knowledge base source badge */}
                        {msg.role === 'assistant' && msg.knowledge_sources?.length > 0 && (
                          <KnowledgeSourceBadge sources={msg.knowledge_sources} />
                        )}

                        {msg.role === 'assistant' && msg.context_window?.length > 0 && (
                          <details className="mt-2 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-elevated)]/70 px-3 py-2">
                            <summary style={{ cursor: 'pointer', fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                              What the model saw
                            </summary>
                            <div className="mt-2 space-y-2">
                              {msg.context_window.map((item, itemIndex) => (
                                <div key={`${item.label || 'context'}-${itemIndex}`} style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                                  <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                                    {item.label || 'Context source'}
                                  </div>
                                  <div>
                                  {[item.source_type, item.artifact_type, item.page ? `p.${item.page}` : null, item.review_state, item.confidence != null ? `${Math.round(item.confidence * 100)}% confidence` : null]
                                      .concat(item.trust_label ? [item.trust_label] : [])
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}

                        {/* Citation chips - open in preview panel */}
                        {msg.role === 'assistant' && msg.citations?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {msg.citations.filter(cit => cit.verified).map((cit, ci) => (
                              <button key={ci}
                                onClick={() => openPreview(cit.ikanoon_url, cit.title || cit.raw_text)}
                                data-testid={`citation-chip-${ci}`}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-all duration-150 citation-verified`}
                                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem', cursor: 'pointer', border: 'none', background: 'none' }}
                                title="Click to preview on Indian Kanoon">
                                ✓ {(cit.title || cit.raw_text).substring(0, 35)}{(cit.title || cit.raw_text).length > 35 ? '...' : ''}
                                <ExternalLink size={9} className="ml-0.5 opacity-60" />
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Actions row */}
                        {msg.role === 'assistant' && !msg.isStreaming && msg.content && (
                          <div className="flex items-center gap-1.5 mt-2">
                            <button onClick={() => handleFeedback(msg.message_id, 1)} className="p-1 rounded transition-colors" style={{ color: 'var(--text-muted)' }}
                              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-primary)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                              <ThumbsUp size={13} />
                            </button>
                            <button onClick={() => handleFeedback(msg.message_id, -1)} className="p-1 rounded transition-colors" style={{ color: 'var(--text-muted)' }}
                              onMouseEnter={e => e.currentTarget.style.color = 'var(--unverified-red)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                              <ThumbsDown size={13} />
                            </button>
                            <button onClick={() => handleCopy(msg.content)} className="p-1 rounded transition-colors" style={{ color: 'var(--text-muted)' }}
                              onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
                              <Copy size={13} />
                            </button>
                            {msg.workflow_plan?.[0] && (
                              <button
                                onClick={() => openWorkflowStep(msg, msg.workflow_plan[0])}
                                className="p-1 rounded transition-colors"
                                title={`Continue in ${msg.workflow_plan[0].agent}`}
                                style={{ color: 'var(--text-muted)' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-primary)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                              >
                                {renderWorkflowIcon(msg.workflow_plan[0].target)}
                              </button>
                            )}
                          </div>
                        )}

                        {msg.role === 'assistant' && !msg.isStreaming && msg.content && msg.workflow_plan?.length > 0 && (
                          <div className="app-chat-workflow-rail" data-testid={`workflow-plan-${idx}`}>
                            <div className="app-chat-workflow-rail__meta">
                              <div>
                                <p className="app-chat-workflow-rail__eyebrow">{msg.workflow_frame || 'Next path'}</p>
                                <p className="app-chat-workflow-rail__summary">{msg.workflow_summary || 'Best next desk sequence from this answer.'}</p>
                              </div>
                            </div>
                            <div className="app-chat-workflow-rail__steps">
                              {msg.workflow_plan.map((step, stepIndex) => (
                                <button
                                  key={`${step.target}-${stepIndex}`}
                                  type="button"
                                  data-testid={`workflow-step-${idx}-${stepIndex}`}
                                  className="app-chat-workflow-step"
                                  onClick={() => openWorkflowStep(msg, step)}
                                >
                                  <span className="app-chat-workflow-step__index">{stepIndex + 1}</span>
                                  <span className="app-chat-workflow-step__body">
                                    <span className="app-chat-workflow-step__title">{step.agent}</span>
                                    <span className="app-chat-workflow-step__label">{step.title}</span>
                                    <span className="app-chat-workflow-step__reason">{step.reason}</span>
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {msg.role === 'assistant' && !msg.isStreaming && msg.content && (
                          <div className="app-chat-response-refinement" data-testid={`response-refine-panel-${idx}`}>
                            <button
                              type="button"
                              data-testid={`response-refine-toggle-${idx}`}
                              className="app-chat-response-refinement__toggle"
                              onClick={() => updateAssistantRefinementState(msg.localId, {
                                open: !msg.response_refinement?.open,
                              })}
                            >
                              Not happy with the response? Let&apos;s fix the prompt to your desired results.
                            </button>
                            {msg.response_refinement?.open && (
                              <div className="app-chat-response-refinement__body">
                                <textarea
                                  data-testid={`response-refine-feedback-${idx}`}
                                  value={msg.response_refinement?.feedback || ''}
                                  onChange={(event) => updateAssistantRefinementState(msg.localId, { feedback: event.target.value })}
                                  className="app-chat-refinement-textarea"
                                  placeholder="Tell Advoq what to change: structure, tone, detail, citations, legal framing, sections to add or remove, or language."
                                  rows={2}
                                />
                                <div className="app-chat-response-refinement__actions">
                                  <button
                                    type="button"
                                    className="app-chat-button"
                                    data-testid={`response-refine-generate-${idx}`}
                                    disabled={!String(msg.response_refinement?.feedback || '').trim() || msg.response_refinement?.loading}
                                    onClick={() => handleGenerateResponseRefinement(msg)}
                                  >
                                    {msg.response_refinement?.loading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                                    Improve brief
                                  </button>
                                  {msg.response_refinement?.draft && (
                                    <button
                                      type="button"
                                      className="app-chat-button"
                                      data-testid={`response-refine-apply-${idx}`}
                                      onClick={() => applyImprovedDraft(msg.response_refinement.draft, {
                                        successMessage: 'Refined brief moved into the composer.',
                                      })}
                                    >
                                      Use in composer
                                    </button>
                                  )}
                                  {msg.response_refinement?.draft && (
                                    <button
                                      type="button"
                                      className="app-chat-button"
                                      data-testid={`response-refine-rerun-${idx}`}
                                      onClick={() => handleSend(msg.response_refinement.draft, { mode: msg.source_mode || mode })}
                                    >
                                      Re-run Chat
                                    </button>
                                  )}
                                </div>
                                {msg.response_refinement?.draft && (
                                  <div className="app-chat-refinement-output">
                                    <textarea
                                      data-testid={`response-refine-draft-${idx}`}
                                      value={msg.response_refinement.draft}
                                      onChange={(event) => updateAssistantRefinementState(msg.localId, { draft: event.target.value })}
                                      className="app-chat-refinement-textarea app-chat-refinement-textarea--output"
                                      rows={4}
                                    />
                                    {msg.response_refinement?.answer_goal ? (
                                      <p className="app-chat-refinement-note">{msg.response_refinement.answer_goal}</p>
                                    ) : null}
                                    {msg.response_refinement?.missing_facts_to_flag?.length > 0 ? (
                                      <p className="app-chat-refinement-note">
                                        Still missing: {msg.response_refinement.missing_facts_to_flag.join(' · ')}
                                      </p>
                                    ) : null}
                                    {msg.workflow_plan?.length > 0 ? (
                                      <div className="app-chat-response-refinement__routes">
                                        {msg.workflow_plan.slice(0, 3).map((step, stepIndex) => (
                                          <button
                                            key={`${step.target}-${stepIndex}`}
                                            type="button"
                                            data-testid={`response-refine-route-${idx}-${stepIndex}`}
                                            className="app-chat-response-refinement__route"
                                            onClick={() => openWorkflowStep(msg, step, {
                                              prefillQuery: msg.response_refinement?.draft || '',
                                            })}
                                          >
                                            {renderWorkflowIcon(step.target, 12)}
                                            Continue in {step.agent}
                                          </button>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Scroll to bottom button */}
            {showScrollButton && (
              <div className="app-chat-scroll shrink-0">
                <button
                  onClick={scrollToBottom}
                  className="app-chat-button app-chat-latest-button"
                  title="Scroll to latest"
                >
                  <ChevronDown size={13} /> Latest
                </button>
              </div>
            )}

            {/* Input area */}
            <div className="app-chat-footer shrink-0">
              <div className="app-chat-footer__inner">
                {/* Linked doc banner */}
                {matterContext && (
                  <div className="app-chat-banner">
                    <Users size={12} className="shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="app-chat-banner__copy truncate">
                      Matter scoped retrieval is active for <strong>{matterContext.name}</strong>
                    </span>
                    <button
                      onClick={() => {
                        setMatterContext(null);
                        const params = new URLSearchParams(location.search);
                        params.delete('matter_id');
                        params.delete('matter_name');
                        const query = params.toString();
                        window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
                      }}
                      className="app-chat-icon-button h-8 w-8 rounded-xl"
                      title="Clear matter context"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}

                {/* Linked doc banner */}
                {linkedDoc && (
                  <div className="app-chat-banner app-chat-banner--accent">
                    <Link2 size={12} className="shrink-0" style={{ color: 'var(--accent-primary)' }} />
                    <span className="app-chat-banner__copy truncate font-medium" style={{ color: 'var(--accent-primary)' }}>
                      {linkedDoc.title}
                    </span>
                    <button
                      onClick={() => { setShowRightPanel(true); setRightPanelTab('history'); }}
                      className="app-chat-button text-xs"
                      title="View chat history for this document"
                    >
                      <History size={10} /> {docHistoryChats.length > 0 ? docHistoryChats.length : ''} History
                    </button>
                    <button onClick={clearLinkedDoc} className="app-chat-icon-button h-8 w-8 rounded-xl" title="Unlink document">
                      <X size={12} />
                    </button>
                  </div>
                )}

                {/* Pending files */}
                {pendingFiles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {pendingFiles.map((file, idx) => (
                      <div key={idx} className="app-chat-banner">
                        {isUploading
                          ? <Loader2 size={13} className="animate-spin shrink-0" style={{ color: 'var(--accent-primary)' }} />
                          : <FileText size={13} className="shrink-0" style={{ color: 'var(--accent-primary)' }} />
                        }
                        <span className="app-chat-banner__copy truncate">
                          {isUploading ? `Indexing "${file.name}"…` : `"${file.name}" — will be uploaded with message`}
                        </span>
                        {!isUploading && (
                          <button onClick={() => removePendingFile(idx)} className="app-chat-icon-button h-8 w-8 rounded-xl">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {composerImprovement?.draft && (
                  <div className="app-chat-refinement-panel" data-testid="composer-improve-panel">
                    <div className="app-chat-refinement-panel__header">
                      <div>
                        <p className="app-chat-refinement-panel__eyebrow">Better brief</p>
                        <p className="app-chat-refinement-panel__copy">
                          {composerImprovement.answer_goal || 'A sharper working brief using the live matter, files, and recent thread context.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="app-chat-icon-button h-8 w-8 rounded-xl"
                        title="Dismiss improved brief"
                        onClick={() => setComposerImprovement(null)}
                      >
                        <X size={12} />
                      </button>
                    </div>
                    <textarea
                      data-testid="composer-improve-draft"
                      value={composerImprovement.draft}
                      onChange={(event) => setComposerImprovement((current) => current ? { ...current, draft: event.target.value, open: true } : current)}
                      className="app-chat-refinement-textarea app-chat-refinement-textarea--output"
                      rows={4}
                    />
                    <div className="app-chat-refinement-panel__footer">
                      <div className="app-chat-refinement-panel__notes">
                        {composerImprovement.missing_facts_to_flag?.length > 0 ? (
                          <span>Still missing: {composerImprovement.missing_facts_to_flag.join(' · ')}</span>
                        ) : <span>Only appears after you click Improve.</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="app-chat-button"
                          data-testid="composer-improve-apply-btn"
                          onClick={() => applyImprovedDraft(composerImprovement.draft)}
                        >
                          Use in composer
                        </button>
                        <button
                          type="button"
                          className="app-chat-button"
                          data-testid="composer-improve-rerun-btn"
                          onClick={() => handleSend(composerImprovement.draft, { mode })}
                        >
                          Run in Chat
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="app-chat-composer">
                  <div className="app-chat-composer__meta">
                    <div className="app-chat-composer__meta-group">
                      <span className="app-chat-composer__pill">{activeMode.label} mode</span>
                      <span className="app-chat-composer__status" title={composerStatus}>{composerStatus}</span>
                    </div>
                    <span className="app-chat-composer__hint">{composerHint}</span>
                  </div>
                  <textarea ref={textareaRef} data-testid="chat-input" value={input}
                    onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                    placeholder="Ask about a matter, draft for filing, or prepare for the next hearing..."
                    className="app-chat-textarea !min-h-[120px] !pr-4 !pb-4 text-base md:!min-h-[84px] md:!pr-[164px] md:!pb-20 md:text-[0.96rem]"
                    rows={1}
                    aria-busy={isStreaming}
                  />
                  <div className="app-chat-composer__actions !static mt-3 flex w-full flex-wrap justify-end gap-2 px-4 pb-4 md:!absolute md:right-4 md:bottom-4 md:mt-0 md:w-auto md:px-0 md:pb-0">
                    {/* File upload button */}
                    <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.txt,.doc" className="hidden" onChange={handleFileSelect} />
                    <button
                      type="button"
                      data-testid="improve-prompt-btn"
                      onClick={handleImprovePrompt}
                      disabled={!input.trim() || isImprovingPrompt}
                      className="app-chat-button"
                      title="Upgrade this prompt using the active matter, files, and chat context"
                    >
                      {isImprovingPrompt ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                      Improve
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isStreaming || isUploading}
                      className="app-chat-icon-button h-11 w-11 min-h-11 min-w-11"
                      style={{ color: pendingFiles.length > 0 ? 'var(--accent-primary)' : 'var(--text-muted)' }}
                      title={`Attach documents (PDF, DOCX, TXT) — ${pendingFiles.length > 0 ? pendingFiles.length + ' queued' : 'select multiple'}`}
                    >
                      <Paperclip size={14} />
                    </button>
                    <JollyDictationButton
                      disabled={isStreaming}
                      compact
                      label="Jolly"
                      onAppendText={appendDictatedText}
                    />
                    {isStreaming && (
                      <button data-testid="stop-generation-btn" onClick={handleStopGeneration}
                        className="app-chat-icon-button h-11 w-11 min-h-11 min-w-11" style={{ color: 'var(--unverified-red)' }} title="Stop generating">
                        <Square size={14} fill="currentColor" />
                      </button>
                    )}
                    <button data-testid="chat-send-btn" onClick={() => handleSend()}
                      disabled={!input.trim() || (isStreaming && !canRestartStream)}
                      className="app-chat-icon-button h-11 w-11 min-h-11 min-w-11"
                      title={canRestartStream ? 'Restart from edited draft' : 'Send'}
                      style={{ color: input.trim() && (!isStreaming || canRestartStream) ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                      <Send size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel (Sources + Doc History) */}
          {showRightPanel && (
            <>
              {isMobile && (
                <button
                  type="button"
                  aria-label="Close sources panel"
                  className="fixed inset-0 z-40 bg-black/40 md:hidden"
                  onClick={() => { setShowRightPanel(false); setSelectedCitation(null); }}
                />
              )}
              <div
                className="app-chat-sidepanel fixed inset-y-0 right-0 z-50 flex w-full max-w-[min(24rem,100vw)] flex-col animate-slide-in-right md:relative md:z-auto md:w-auto md:max-w-none"
                style={{ width: isMobile ? undefined : rightPanelWidth, position: isMobile ? 'fixed' : 'relative' }}
                data-testid="citations-panel"
              >
              {/* Drag handle */}
              <div
                onMouseDown={e => startRightDrag(e, -1)}
                className="hidden md:block"
                style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                  cursor: 'col-resize', zIndex: 10,
                  background: 'transparent',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-primary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              />
              {/* Panel header with tabs */}
              <div className="app-chat-sidepanel__header shrink-0 p-3 pb-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="app-chat-sidepanel__tabs">
                    <button onClick={() => setRightPanelTab('sources')}
                      className={`app-chat-sidepanel__tab ${rightPanelTab === 'sources' ? 'is-active' : ''}`}>
                      Sources
                    </button>
                    <button onClick={() => setRightPanelTab('history')}
                      className={`app-chat-sidepanel__tab ${rightPanelTab === 'history' ? 'is-active' : ''}`}>
                      <History size={10} /> Doc Chats {docHistoryChats.length > 0 && <span className="text-xs px-1 rounded-full" style={{ background: 'var(--accent-primary)', color: 'var(--bg-base)', fontSize: '0.6rem' }}>{docHistoryChats.length}</span>}
                    </button>
                  </div>
                  <button onClick={() => { setShowRightPanel(false); setSelectedCitation(null); }} className="app-chat-icon-button app-chat-sidepanel__close">
                    <X size={14} style={{ color: 'var(--text-muted)' }} />
                  </button>
                </div>
              </div>

              <div className="app-chat-sidepanel__body flex-1 overflow-y-auto p-4">
                {/* SOURCES TAB */}
                {rightPanelTab === 'sources' && (
                  selectedCitation ? (
                    <div className="space-y-3">
                      <button onClick={() => setSelectedCitation(null)} className="app-chat-sidepanel__back">&larr; Back to all</button>
                      <h4 className="text-sm font-semibold leading-snug" style={{ fontFamily: 'var(--font-display)' }}>
                        {selectedCitation.title || selectedCitation.raw_text}
                      </h4>
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedCitation.verified ? 'citation-verified' : 'citation-unverified'}`}>
                        {selectedCitation.verified ? 'Verified' : 'Unverified'}
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>Type: {selectedCitation.type}</p>
                      <div className="space-y-2">
                        {selectedCitation.ikanoon_url && (
                          <button onClick={() => openPreview(selectedCitation.ikanoon_url, selectedCitation.title || selectedCitation.raw_text)}
                            className="app-chat-sidepanel__action">
                            Preview on Indian Kanoon <ExternalLink size={11} />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {knowledgeSourcesPanel.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                            <CheckCircle size={10} /> Context used in replies
                          </p>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {trustSummary.map((item) => (
                              <SourceTrustBadge
                                key={item.label}
                                label={item.label}
                                compact
                                count={item.count}
                              />
                            ))}
                          </div>
                          <div className="space-y-1.5">
                            {knowledgeSourcesPanel.map((source, index) => {
                              const previewHref = getSourcePreviewHref(source, {
                                uploadPreviewBuilder: getUploadPreviewUrl,
                              });
                              const sourceTitle = getSourceDisplayTitle(source);
                              const detail = [
                                source.docsource,
                                (source.pageNumber || source.page_number) ? `p.${source.pageNumber || source.page_number}` : null,
                                (source.reviewState || source.review_state) ? String(source.reviewState || source.review_state).replace(/_/g, ' ') : null,
                              ].filter(Boolean).join(' · ');

                              return (
                                <div
                                  key={`${sourceTitle}-${index}`}
                                  className="app-chat-sidepanel__card w-full text-left p-2.5 rounded-md"
                                >
                                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                    <SourceTrustBadge source={source} compact />
                                    {detail ? (
                                      <span className="text-xs" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                                        {detail}
                                      </span>
                                    ) : null}
                                  </div>
                                  <p className="text-xs" style={{ color: 'var(--text-primary)' }}>{sourceTitle}</p>
                                  {source.reason && (
                                    <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                                      {source.reason}
                                    </p>
                                  )}
                                  {previewHref && (
                                    <button
                                      onClick={() => openPreview(previewHref, sourceTitle)}
                                      className="mt-2 text-xs flex items-center gap-1"
                                      style={{ color: 'var(--accent-primary)' }}
                                    >
                                      Open source <ExternalLink size={10} />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {docSources.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: 'var(--accent-primary)', fontSize: '0.65rem' }}>
                            <FileText size={10} /> Your Documents
                          </p>
                          <div className="space-y-1.5">
                            {[...new Map(docSources.map(d => [d.upload_id, d])).values()].map((ds, i) => (
                              <button
                                key={i}
                                onClick={() => ds.upload_id && openPreview(getUploadPreviewUrl(ds.upload_id), ds.filename)}
                                className="app-chat-sidepanel__card w-full text-left p-2.5 rounded-md"
                              >
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <CheckCircle size={9} style={{ color: 'var(--verified-green)' }} />
                                  <span className="text-xs font-medium" style={{ color: 'var(--verified-green)', fontSize: '0.65rem' }}>Your Document</span>
                                </div>
                                <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{ds.filename}</p>
                                {ds.summary && <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>{ds.summary}</p>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {citations.filter(cit => cit.verified && cit.ikanoon_url).length > 0 && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                            <ExternalLink size={10} /> Indian Kanoon
                          </p>
                          <div className="space-y-1.5">
                            {citations.filter(cit => cit.verified && cit.ikanoon_url).map((cit, i) => (
                              <button key={i} onClick={() => setSelectedCitation(cit)}
                                className="app-chat-sidepanel__card w-full text-left p-2.5 rounded-md">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="text-xs citation-verified px-1 py-0.5 rounded" style={{ fontSize: '0.65rem' }}>
                                    ✓
                                  </span>
                                  <span className="text-xs" style={{ color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem' }}>{cit.type}</span>
                                </div>
                                <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{cit.title || cit.raw_text}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {citations.length === 0 && docSources.length === 0 && (
                        <p className="text-xs py-4" style={{ color: 'var(--text-muted)' }}>Citations and document sources appear here as the AI responds.</p>
                      )}
                    </div>
                  )
                )}

                {/* DOC HISTORY TAB */}
                {rightPanelTab === 'history' && (
                  <div className="space-y-3">
                    {linkedDoc ? (
                      <>
                        <div className="app-chat-sidepanel__summary p-3 rounded-lg">
                          <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{linkedDoc.title}</p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{linkedDoc.type.replace('_', ' ')}</p>
                          <div className="flex gap-2 mt-2">
                            {linkedDoc.type === 'document' && (
                              <button
                                type="button"
                                onClick={() => navigate(`/documents/${linkedDoc.id}`)}
                                className={`${SECONDARY_BUTTON_CLASS} inline-flex items-center gap-1.5`}
                              >
                                <ExternalLink size={10} /> Open Doc
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={startNewDocChat}
                              className={`${PRIMARY_BUTTON_CLASS} inline-flex items-center gap-1.5`}
                            >
                              <Plus size={10} /> New Chat
                            </button>
                            {['upload', 'saved_item'].includes(linkedDoc.type) && (
                              <button
                                type="button"
                                onClick={moveLinkedResourceToTrash}
                                className={`${DANGER_BUTTON_CLASS} inline-flex items-center gap-1.5`}
                              >
                                <Trash2 size={10} /> Trash
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                          Previous Chats on this Doc ({docHistoryChats.length})
                        </p>
                        {docHistoryChats.length === 0 ? (
                          <CompactEmptyState
                            icon={History}
                            title="No document chat history yet"
                            description="Start a thread on this document and Advoq will keep every follow-up attached here."
                          />
                        ) : (
                          docHistoryChats.map(ch => (
                            <div
                              key={ch.chat_id}
                              className={`app-chat-sidepanel__card w-full p-2 rounded-md ${ch.chat_id === chatId ? 'is-active' : ''}`}
                              style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                            >
                              <button
                                onClick={() => navigate(`/chat/${ch.chat_id}?linked_doc_id=${linkedDoc.id}&linked_doc_type=${linkedDoc.type}&linked_doc_title=${encodeURIComponent(linkedDoc.title)}`)}
                                className="w-full text-left"
                                style={{ minWidth: 0 }}
                              >
                                <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{ch.title}</p>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                                  {new Date(ch.updated_at).toLocaleDateString()} · {ch.mode}
                                </p>
                              </button>
                              <button
                                onClick={() => moveChatToTrash(ch.chat_id, {
                                  title: ch.title || 'Chat',
                                  clearCurrent: ch.chat_id === chatId,
                                })}
                                className="app-chat-icon-button h-8 w-8 rounded-xl"
                                title="Move chat to trash"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))
                        )}
                      </>
                    ) : (
                      <CompactEmptyState
                        icon={FolderOpen}
                        title="Link a document to this thread"
                        description="Attach a draft, upload, or saved note so chat can stay grounded in the exact record you care about."
                        action={(
                          <button
                            type="button"
                            onClick={openDocPicker}
                            className={`${SECONDARY_BUTTON_CLASS} inline-flex items-center gap-1.5`}
                          >
                            <FolderOpen size={14} />
                            Open saved document
                          </button>
                        )}
                      />
                    )}
                  </div>
                )}
              </div>
              </div>
            </>
          )}
        </div>

        {/* Doc Picker Modal */}
        {showDocPicker && (
          <div className="app-modal-backdrop">
            <div className="app-modal app-modal--md flex flex-col" style={{ maxHeight: '80vh' }}>
              <div className="app-modal__header shrink-0" style={{ paddingBottom: '18px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div>
                  <h3 className="app-modal__title">Open Saved Document</h3>
                  <p className="app-modal__description">Link a document, upload, or saved item to this thread.</p>
                </div>
                <button onClick={() => setShowDocPicker(false)} className="app-chat-icon-button">
                  <X size={16} />
                </button>
              </div>
              <div className="flex gap-1 p-3 shrink-0" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                {[
                  { id: 'documents', label: 'My Documents', count: pickerDocs.length },
                  { id: 'uploads', label: 'Uploads', count: pickerUploads.length },
                  { id: 'saved', label: 'Saved Items', count: pickerSaved.length },
                ].map(t => (
                  <button key={t.id} onClick={() => setPickerTab(t.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium"
                    style={{ background: pickerTab === t.id ? 'var(--bg-elevated)' : 'transparent', color: pickerTab === t.id ? 'var(--text-primary)' : 'var(--text-muted)', border: `1px solid ${pickerTab === t.id ? 'var(--border-default)' : 'transparent'}` }}>
                    {t.label}
                    {t.count > 0 && <span className="text-xs px-1 rounded-full" style={{ background: 'var(--bg-hover)', fontSize: '0.6rem' }}>{t.count}</span>}
                  </button>
                ))}
              </div>
              <div className="px-3 py-2 shrink-0">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                  <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
                    className="advoq-input w-full pl-8 text-xs" placeholder="Search..." />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
                {pickerLoading ? (
                  <PickerListSkeleton />
                ) : (
                  <>
                    {pickerTab === 'documents' && (
                      pickerDocs.filter(d => !pickerSearch || d.title.toLowerCase().includes(pickerSearch.toLowerCase())).length === 0
                        ? (
                          <CompactEmptyState
                            icon={FileText}
                            title="No documents found"
                            description={pickerSearch ? 'Try a different title or clear the search to browse your drafting workspace.' : 'Your drafting workspace is empty. Create or save a document to link it here.'}
                          />
                        )
                        : pickerDocs.filter(d => !pickerSearch || d.title.toLowerCase().includes(pickerSearch.toLowerCase())).map(doc => (
                          <button key={doc.doc_id} onClick={() => selectDocFromPicker(doc.doc_id, 'document', doc.title)}
                            className="w-full text-left p-3 rounded-lg flex items-start gap-3 transition-all duration-150"
                            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}>
                            <FileText size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{doc.title}</p>
                              <p className="text-xs" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>{doc.type?.replace(/_/g, ' ')} · {new Date(doc.updated_at).toLocaleDateString()}</p>
                            </div>
                          </button>
                        ))
                    )}
                    {pickerTab === 'uploads' && (
                      pickerUploads.filter(u => !pickerSearch || u.filename.toLowerCase().includes(pickerSearch.toLowerCase())).length === 0
                        ? (
                          <CompactEmptyState
                            icon={Paperclip}
                            title="No uploads found"
                            description={pickerSearch ? 'Nothing matched that filename yet. Try a shorter search term.' : 'Upload a brief, order, or note in chat first and it will show up here for quick reuse.'}
                          />
                        )
                        : pickerUploads.filter(u => !pickerSearch || u.filename.toLowerCase().includes(pickerSearch.toLowerCase())).map(u => (
                          <button key={u.upload_id} onClick={() => selectDocFromPicker(u.upload_id, 'upload', u.filename)}
                            className="w-full text-left p-3 rounded-lg flex items-start gap-3 transition-all duration-150"
                            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}>
                            <Paperclip size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--text-secondary)' }} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{u.filename}</p>
                              {u.summary && <p className="text-xs line-clamp-2" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>{u.summary}</p>}
                            </div>
                          </button>
                        ))
                    )}
                    {pickerTab === 'saved' && (
                      pickerSaved.filter(s => !pickerSearch || s.title.toLowerCase().includes(pickerSearch.toLowerCase())).length === 0
                        ? (
                          <CompactEmptyState
                            icon={Bookmark}
                            title="No saved items found"
                            description={pickerSearch ? 'Try another keyword or browse without filters to see your full saved library.' : 'Save a research note, chat answer, or brief and you can link it into this thread later.'}
                          />
                        )
                        : pickerSaved.filter(s => !pickerSearch || s.title.toLowerCase().includes(pickerSearch.toLowerCase())).map(s => (
                          <button key={s.item_id} onClick={() => selectDocFromPicker(s.item_id, 'saved_item', s.title)}
                            className="w-full text-left p-3 rounded-lg flex items-start gap-3 transition-all duration-150"
                            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}>
                            <Bookmark size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{s.title}</p>
                              <p className="text-xs line-clamp-2" style={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}>{s.content?.substring(0, 80)}</p>
                            </div>
                          </button>
                        ))
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {previewUrl && (
        <LinkPreviewPanel
          url={previewUrl}
          title={previewTitle}
          onClose={() => { setPreviewUrl(null); setPreviewTitle(''); }}
        />
      )}
    </div>
  );
}
